/**
 * CRSStatusDisplay - Interactive CRS indicator in the bottom-right status bar
 *
 * Displays active EPSG code & short name. Clicking opens the CRS selection dialog.
 *
 * Validates: Requirements 44.1, 44.2
 */
import React from 'react';
import { Typography, Box, Tooltip, Chip } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { openCRSSelector } from '../store/slices/coordinateSystemSlice';
import { getCRSDisplayName } from '../services/CoordinateSystemManager';

const CRSStatusDisplay: React.FC = () => {
  const dispatch = useAppDispatch();
  const mapCRS = useAppSelector((state) => state.coordinateSystem.mapCRS);

  const displayName = getCRSDisplayName(mapCRS);

  const handleClick = () => {
    dispatch(openCRSSelector());
  };

  return (
    <Tooltip title="Click to change map projection / CRS" placement="top" arrow>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: 'pointer',
          px: 1,
          py: 0.25,
          borderRadius: '12px',
          transition: 'all 0.2s ease',
          backgroundColor: 'rgba(79,195,247,0.08)',
          border: '1px solid rgba(79,195,247,0.2)',
          '&:hover': {
            backgroundColor: 'rgba(79,195,247,0.18)',
            borderColor: 'rgba(79,195,247,0.4)',
            transform: 'translateY(-1px)',
            '& .crs-text': {
              color: '#ffffff',
            },
          },
        }}
        data-testid="crs-status-display"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
          }
        }}
        aria-label={`Current CRS: ${displayName}. Click to change map projection.`}
      >
        <PublicIcon sx={{ fontSize: 13, color: '#4fc3f7' }} />
        <Typography
          className="crs-text"
          sx={{
            color: '#e0e0e0',
            fontSize: '0.72rem',
            fontWeight: 600,
            fontFamily: 'monospace',
            transition: 'color 0.15s',
          }}
        >
          {displayName}
        </Typography>
        <Chip
          label="Change"
          size="small"
          sx={{
            height: 16,
            fontSize: '0.6rem',
            fontWeight: 600,
            backgroundColor: 'rgba(255,255,255,0.15)',
            color: '#81d4fa',
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default CRSStatusDisplay;
